/**
 * ============================================================================
 * DISCLAIMER BADGE - Development/Testing Phase Notice
 * ============================================================================
 *
 * A pill-style badge that appears on all pages to inform visitors that
 * this project is still in the testing and development phase.
 * Clicking opens a modal with full project status disclosure.
 *
 * Features:
 * - Pill-style single-row badge (like wallet connection banner)
 * - Fixed position below navbar (accounts for wallet banner if present)
 * - Clickable to open DisclaimerModal with full details
 * - Dismissible via X button (saved to localStorage)
 * - ARIA accessible
 *
 * @author Ethereal Offering Team
 * @version 0.4 - January 2026
 */

import React, { useState, useEffect } from 'react';
import { X, Construction, ChevronRight } from 'lucide-react';
import DisclaimerModal from './modals/DisclaimerModal';

const STORAGE_KEY = 'ethereal_disclaimer_dismissed';

export default function DisclaimerBadge() {
  const [isDismissed, setIsDismissed] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bannerTop, setBannerTop] = useState('calc(var(--nav-height, 80px) + 8px)');
  const [anyModalOpen, setAnyModalOpen] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    setIsDismissed(dismissed === 'true');
  }, []);

  // Track other modal visibility to hide badge
  useEffect(() => {
    const handler = (e) => setAnyModalOpen(!!e.detail?.isOpen);
    window.addEventListener('modalVisibilityChange', handler);
    return () => window.removeEventListener('modalVisibilityChange', handler);
  }, []);

  // Calculate position below navbar + wallet banner if present
  useEffect(() => {
    const updateTop = () => {
      try {
        const nav = document.querySelector('nav, #navbar, .navbar');
        const walletBanner = document.querySelector('[data-wallet-banner]');
        let top = 80;
        if (nav) top = nav.getBoundingClientRect().height;
        // Add extra offset if wallet banner is visible
        if (walletBanner && walletBanner.offsetHeight > 0) {
          top += 52; // wallet banner height + gap
        }
        setBannerTop(`${Math.round(top) + 8}px`);
      } catch {
        setBannerTop('calc(var(--nav-height, 80px) + 60px)');
      }
    };
    updateTop();
    window.addEventListener('resize', updateTop);
    // Re-check periodically for wallet banner changes
    const interval = setInterval(updateTop, 500);
    return () => {
      window.removeEventListener('resize', updateTop);
      clearInterval(interval);
    };
  }, []);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setIsDismissed(true);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  // Determine if badge should be visible
  // Hide when dismissed, when our modal is open, or when another modal is open
  const showBadge = !isDismissed && !isModalOpen && !anyModalOpen;

  return (
    <>
      {/* Badge - conditionally rendered */}
      {showBadge && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Project status notice. Click to learn more about this demo concept."
          aria-haspopup="dialog"
          onClick={() => setIsModalOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setIsModalOpen(true); }}
          style={{
            position: 'fixed',
            top: bannerTop,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10003,
            display: 'flex',
            justifyContent: 'center',
            width: 'fit-content',
            maxWidth: 'calc(100% - 40px)',
            cursor: 'pointer',
          }}
        >
        <div style={{
          background: 'linear-gradient(135deg, rgba(26, 11, 46, 0.85), rgba(17, 24, 39, 0.85))',
          border: '1px solid rgba(251, 191, 36, 0.35)',
          borderRadius: '16px',
          padding: '10px 18px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 20px rgba(251, 191, 36, 0.15)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}>
          <Construction size={18} color="#fbbf24" />
          <span style={{
            color: 'rgba(255, 255, 255, 0.95)',
            fontSize: '0.875rem',
            fontWeight: 500,
            letterSpacing: '0.02em',
          }}>
            Demo / Concept Notice
          </span>
          <ChevronRight size={16} color="rgba(251, 191, 36, 0.7)" />
          {/* Dismiss X button */}
          <button
            onClick={handleDismiss}
            aria-label="Dismiss disclaimer badge"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(251, 191, 36, 0.15)',
              border: '1px solid rgba(251, 191, 36, 0.25)',
              borderRadius: '50%',
              padding: '5px',
              marginLeft: '4px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(251, 191, 36, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(251, 191, 36, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(251, 191, 36, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(251, 191, 36, 0.25)';
            }}
          >
            <X size={14} color="rgba(251, 191, 36, 0.9)" />
          </button>
        </div>
      </div>
      )}

      {/* Disclaimer Modal - always rendered so it can open */}
      <DisclaimerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}

